import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Stack,
  Typography,
  Paper,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
} from '@mui/icons-material';
import { LeadCard } from './LeadCard';
import { LeadForm } from './LeadForm';
import { useLeadsStore } from '../../store/leadsStore';
import { leadsService } from '../../services/leads.service';
import { Lead, LeadFormData, LeadStatus, LeadPriority } from '../../types/lead.types';
import { Loading } from '../common/Loading';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';

export const LeadsList: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useAuthStore();
  const { leads, setLeads, setLoading, isLoading, addLead, updateLead: updateLeadInStore } = useLeadsStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<LeadStatus | 'all'>('all');
  const [priorityFilter, setPriorityFilter] = useState<LeadPriority | 'all'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showForm, setShowForm] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  
  // Check org_role for permissions
  const canEdit = profile?.org_role !== 'viewer';

  const loadLeads = useCallback(async () => {
    try {
      setLoading(true);
      const data = await leadsService.getLeads();
      setLeads(data);
    } catch (error: any) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [setLoading, setLeads]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const handleCreateLead = async (values: LeadFormData) => {
    try {
      const newLead = await leadsService.createLead(values);
      addLead(newLead);
      toast.success('Lead created successfully');
      setShowForm(false);
    } catch (error: any) {
      // Show specific error message for duplicates
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create lead';
      toast.error(errorMessage, { duration: 5000 });
      
      // Keep form open so user can fix the duplicate issue
      console.error('Create lead error:', error);
    }
  };

  const handleUpdateLead = async (values: LeadFormData) => {
    if (!editingLead) return;
    
    try {
      const updated = await leadsService.updateLead(editingLead.id, values);
      updateLeadInStore(editingLead.id, updated);
      toast.success('Lead updated successfully');
      setEditingLead(null);
      setShowForm(false);
    } catch (error: any) {
      // Show specific error message for duplicates
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update lead';
      toast.error(errorMessage, { duration: 5000 });
      
      // Keep form open so user can fix the duplicate issue
      console.error('Update lead error:', error);
    }
  };

  const handleEdit = (lead: Lead) => {
    setEditingLead(lead);
    setShowForm(true);
  };

  const handleView = (lead: Lead) => {
    navigate(`/leads/${lead.id}`);
  };

  const filteredLeads = leads.filter((lead) => {
    const matchesSearch =
      lead.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || lead.priority === priorityFilter;

    return matchesSearch && matchesStatus && matchesPriority;
  });

  if (isLoading) {
    return <Loading message="Loading leads..." />;
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            Leads Management
          </Typography>
          {canEdit && (
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={() => setShowForm(true)}
            >
              Add New Lead
            </Button>
          )}
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Box sx={{ position: 'relative', flexGrow: 1 }}>
            <SearchIcon sx={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'text.secondary', zIndex: 1 }} />
            <TextField
              placeholder="Search leads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ 
                flexGrow: 1,
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  paddingLeft: '40px',
                }
              }}
            />
          </Box>

          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as LeadStatus | 'all')}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="new">New</MenuItem>
            <MenuItem value="contacted">Contacted</MenuItem>
            <MenuItem value="qualified">Qualified</MenuItem>
            <MenuItem value="proposal">Proposal</MenuItem>
            <MenuItem value="negotiation">Negotiation</MenuItem>
            <MenuItem value="won">Won</MenuItem>
            <MenuItem value="lost">Lost</MenuItem>
          </TextField>

          <TextField
            select
            label="Priority"
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value as LeadPriority | 'all')}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">All Priority</MenuItem>
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="urgent">Urgent</MenuItem>
          </TextField>

          <Box>
            <IconButton onClick={() => setViewMode('grid')} color={viewMode === 'grid' ? 'primary' : 'default'}>
              <ViewModuleIcon />
            </IconButton>
            <IconButton onClick={() => setViewMode('list')} color={viewMode === 'list' ? 'primary' : 'default'}>
              <ViewListIcon />
            </IconButton>
          </Box>
        </Stack>
      </Paper>

      {filteredLeads.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No leads found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Try adjusting your filters or create a new lead
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 3 }}>
          {filteredLeads.map((lead) => (
            <LeadCard 
              key={lead.id} 
              lead={lead} 
              onEdit={handleEdit} 
              onView={handleView}
              onAssignmentChange={loadLeads}
            />
          ))}
        </Box>
      )}

      <LeadForm
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingLead(null);
        }}
        onSubmit={editingLead ? handleUpdateLead : handleCreateLead}
        initialValues={editingLead || undefined}
        title={editingLead ? 'Edit Lead' : 'Create New Lead'}
      />
    </Box>
  );
};
